/**
 * API Debugger for RTK Query
 */

const isDebugEnabled = () => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('rtkQueryDebug') === 'true';
};

/**
 * Parse stack trace to find the component that initiated the API call
 * Looks for React component files (.jsx, .js) in the stack trace
 * Excludes internal RTK Query and Redux files
 */
const parseStackTrace = () => {
    try {
        // Capture a MUCH longer stack trace to find the component
        const oldLimit = Error.stackTraceLimit;
        Error.stackTraceLimit = 50;

        const error = new Error();
        const stack = error.stack || '';
        const lines = stack.split('\n');

        // Restore original limit
        Error.stackTraceLimit = oldLimit;

        // Patterns to identify component files (not library/framework files)
        const componentFilePatterns = [
            /webpack-internal:\/\/\/\.\/src\/react\/.*\.(jsx?|tsx?)/, // Webpack internal paths
            /\/src\/react\/.*\.(jsx?|tsx?)/, // Source files in react folder
            /src\/react\/.*\.(jsx?|tsx?)/, // Without leading slash
            /\.\/src\/react\/.*\.(jsx?|tsx?)/, // With ./ prefix
            /\/components\/.*\.(jsx?|tsx?)/, // Component files
            /\/pages\/.*\.(jsx?|tsx?)/, // Page files
            /\/hooks\/.*\.(jsx?|tsx?)/, // Custom hooks
            /\/providers\/.*\.(jsx?|tsx?)/, // Providers
            /\/containers\/.*\.(jsx?|tsx?)/, // Containers
            /\/views\/.*\.(jsx?|tsx?)/, // Views
            /src\/.*\.(jsx?|tsx?)/, // Any file in src with jsx/tsx extension
            /react\/.*\.(jsx?|tsx?)/, // Any react file
            /\/react\/.*\.(jsx?|tsx?)/, // React with leading slash
            /\.jsx:/, // Any .jsx file (with colon for line number)
            /\.tsx:/, // Any .tsx file (with colon for line number)
            /\.jsx\?/, // Any .jsx file (with query string)
            /\.js:/, // Any .js file (with colon for line number)
            /\.js\?/, // Any .js file (with query string)
        ];

        // Patterns to exclude (framework/library files)
        const excludePatterns = [
            /node_modules/,
            /apiDebugger\.js/,
            /^Error$/, // The "Error" line itself
            /^\s*at parseStackTrace/, // Our own function
            /^\s*at logApiCall/, // Our own function
            /^\s*at addInitiatorHeaders/, // Our own function
        ];

        // First pass: Look for component files
        let foundComponent = null;

        for (const line of lines) {
            // Skip if it matches exclude patterns
            if (excludePatterns.some((pattern) => pattern.test(line))) {
                continue;
            }

            // Skip api.js but keep looking
            if (line.includes('/api.js:') || line.includes('\\api.js:')) {
                continue;
            }

            // Check if it matches component file patterns
            if (componentFilePatterns.some((pattern) => pattern.test(line))) {
                // Extract file path and line number
                // Format: "    at functionName (path/to/file.js:123:45)"
                // Or: "    at path/to/file.js:123:45"
                const match =
                    line.match(/\((.*):(\d+):(\d+)\)/) ||
                    line.match(/at\s+(.*):(\d+):(\d+)/) ||
                    line.match(/(.*):(\d+):(\d+)/);

                if (match) {
                    const [, fullPath, lineNum, colNum] = match;

                    // Try different path extraction strategies
                    let displayPath = fullPath;

                    // Strategy 1: Handle webpack-internal paths
                    const webpackInternalMatch = fullPath.match(/webpack-internal:\/\/\/\.\/(.+)/);
                    if (webpackInternalMatch) {
                        displayPath = webpackInternalMatch[1];
                    }
                    // Strategy 2: Extract src/react/... path
                    else {
                        const srcReactMatch = fullPath.match(/(src\/react\/[^?]+)/);
                        if (srcReactMatch) {
                            displayPath = srcReactMatch[1];
                        }
                        // Strategy 3: Extract any src/... path
                        else {
                            const srcMatch = fullPath.match(/(src\/[^?]+)/);
                            if (srcMatch) {
                                displayPath = srcMatch[1];
                            }
                            // Strategy 4: Extract file name with extension
                            else {
                                const fileMatch = fullPath.match(/([^\/]+\.(jsx?|tsx?))([?:]|$)/);
                                if (fileMatch) {
                                    displayPath = fileMatch[1];
                                }
                                // Strategy 5: Just clean up the path
                                else {
                                    displayPath = fullPath
                                        .replace(/^.*\//, '')
                                        .replace(/\?.*$/, '')
                                        .replace(/^.*webpack-internal:\/\/\/\.\//, '')
                                        .replace(/^http:\/\/.*?\//, '')
                                        .replace(/^https:\/\/.*?\//, '');
                                }
                            }
                        }
                    }

                    // Clean up the display path
                    displayPath = displayPath
                        .replace(/\?.*$/, '') // Remove query strings
                        .replace(/^\.\//, ''); // Remove ./ prefix

                    foundComponent = {
                        file: displayPath,
                        line: lineNum,
                        column: colNum,
                        fullLine: line.trim(),
                    };

                    return foundComponent;
                }
            }
        }

        if (foundComponent) {
            return foundComponent;
        }

        // Fallback: try to find ANY .jsx/.js file
        for (const line of lines) {
            // Skip our own files and node_modules
            if (line.includes('apiDebugger') || line.includes('node_modules')) {
                continue;
            }

            // Look for any .jsx, .tsx, or src/react file
            if (line.includes('.jsx') || line.includes('.tsx') || line.includes('src/react')) {
                const match =
                    line.match(/webpack-internal:\/\/\/\.\/(.+):(\d+):(\d+)/) ||
                    line.match(/\((.+):(\d+):(\d+)\)/) ||
                    line.match(/at\s+(.+):(\d+):(\d+)/);

                if (match) {
                    const [, fullPath, lineNum, colNum] = match;

                    let displayPath = fullPath;
                    const srcMatch = fullPath.match(/(src\/react\/.+?)(:|\?|$)/);
                    if (srcMatch) {
                        displayPath = srcMatch[1];
                    }

                    return {
                        file: displayPath,
                        line: lineNum,
                        column: colNum,
                        fullLine: line.trim(),
                    };
                }
            }
        }

        return null;
    } catch (error) {
        console.warn('Failed to parse stack trace:', error);
        return null;
    }
};

/**
 * Format the API endpoint URL for display
 */
const formatEndpoint = (args) => {
    try {
        if (typeof args === 'string') {
            return args;
        }

        if (typeof args === 'object' && args !== null) {
            return args.url || JSON.stringify(args);
        }

        return String(args);
    } catch (error) {
        return 'unknown';
    }
};

/**
 * Get HTTP method from request args
 */
const getMethod = (args) => {
    try {
        if (typeof args === 'object' && args?.method) {
            return args.method;
        }
        return 'GET';
    } catch (error) {
        return 'GET';
    }
};

/**
 * Get color for HTTP method
 */
const getMethodColor = (method) => {
    const colors = {
        GET: '#61AFFE',
        POST: '#49CC90',
        PUT: '#FCA130',
        DELETE: '#F93E3E',
        PATCH: '#50E3C2',
    };
    return colors[method] || '#999';
};

/**
 * Derive the RTK Query hook name from endpoint name and method
 */
const deriveHookName = (endpointName, method) => {
    try {
        if (!endpointName) return null;

        // Capitalize first letter of endpoint
        const pascalCase = endpointName.charAt(0).toUpperCase() + endpointName.slice(1);

        // Determine if it's a Query or Mutation based on HTTP method
        const suffix = method === 'GET' ? 'Query' : 'Mutation';

        return `use${pascalCase}${suffix}`;
    } catch (error) {
        return null;
    }
};

/**
 * Log API call with initiator information
 */
export const logApiCall = (args, api) => {
    try {
        if (!isDebugEnabled()) return null;

        // Safety checks
        if (!args || !api) return null;

        const initiator = parseStackTrace();
        const endpoint = formatEndpoint(args);
        const method = getMethod(args);
        const methodColor = getMethodColor(method);
        const timestamp = new Date().toLocaleTimeString();

        // Console output with styling
        const initiatorDisplay = initiator ? initiator.file.split('/').pop() : 'unknown';

        console.groupCollapsed(
            `%c[RTK Query] %c${method} %c${endpoint} %c@ %c${initiatorDisplay}`,
            'color: #764ABC; font-weight: bold;', // RTK Query label
            `color: ${methodColor}; font-weight: bold;`, // HTTP method
            'color: #333; font-weight: normal;', // Endpoint
            'color: #666; font-style: italic;', // @ symbol
            'color: #2196F3;', // Initiator file name
        );

        // Make initiator path clickable by logging Error object
        if (initiator) {
            const webpackPath = `webpack://phoenix-ui/${initiator.file}`;
            const componentName = initiator.file.split('/').pop().replace('.jsx', '').replace('.js', '');
            console.log('🎯 Initiator:');

            // Log Error object - Chrome makes stack traces clickable!
            const err = new Error(componentName);
            err.name = '   at';
            err.stack = `   at ${componentName} (${webpackPath}:${initiator.line}:${initiator.column})`;
            console.error(err);
        } else {
            console.log('🎯 Initiator:', 'Unknown');
        }
        console.log('⏰ Time:', timestamp);
        console.log('🔧 Endpoint Type:', api.endpoint);

        // Derive and show the RTK Query hook name
        const hookName = deriveHookName(api.endpoint, method);
        if (hookName) {
            console.log('🪝 Hook:', hookName);
        }

        // Show note about source map accuracy
        if (initiator && initiator.column > 100) {
            console.log(
                '%c⚠️  Note: High column number suggests webpack source map may be inaccurate',
                'color: #FCA130;',
            );
        }

        if (typeof args === 'object' && args !== null) {
            if (args.body) {
                console.log('📦 Request Body:', args.body);
            }
            if (args.params) {
                console.log('🔍 Params:', args.params);
            }
        }

        console.groupEnd();
    } catch (error) {
        // Silently fail to prevent breaking the app
        console.error('[RTK Query Debugger] Error in logApiCall:', error);
    }
};

/**
 * Create custom headers with initiator information for network debugging
 * This adds headers to the actual HTTP request that can be seen in the Network tab
 */
export const addInitiatorHeaders = (headers, args) => {
    try {
        if (!isDebugEnabled()) return headers;

        const initiator = parseStackTrace();

        if (initiator) {
            // Add custom headers (X- prefix for custom headers)
            // Note: These may be stripped by the server, but visible in browser DevTools
            headers.set('X-RTK-Initiator-File', initiator.file);
            headers.set('X-RTK-Initiator-Line', initiator.line);
            headers.set('X-RTK-Component-Stack', initiator.fullLine.substring(0, 200)); // Limit length
        }

        return headers;
    } catch (error) {
        // Silently fail and return original headers
        console.error('[RTK Query Debugger] Error in addInitiatorHeaders:', error);
        return headers;
    }
};

/**
 * Utility to enable/disable debugging from anywhere
 */
export const enableApiDebug = () => {
    localStorage.setItem('rtkQueryDebug', 'true');
    console.log('✅ API Debug enabled. Reload to see API call logs.');
};

export const disableDebug = () => {
    localStorage.removeItem('rtkQueryDebug');
    console.log('❌ API debugging disabled. Reload to apply.');
};

// Expose debugging controls globally for easy access from browser console
if (typeof window !== 'undefined') {
    window.enableApiDebug = enableApiDebug;
    window.disableDebug = disableDebug;

    // Log current status on load (only if debugging is enabled)
    if (isDebugEnabled()) {
        console.log(
            '%c[RTK Query Debugger] %cAPI debugging enabled',
            'color: #764ABC; font-weight: bold;',
            'color: #49CC90;',
        );
        console.log('To disable: window.disableDebug()');
    }
}

export default {
    logApiCall,
    addInitiatorHeaders,
    enableApiDebug,
    disableDebug,
    isDebugEnabled,
};
